import {Dialog, Pane, Paragraph} from 'evergreen-ui';
import React, {memo} from 'react';
import {FatOutlineButton} from '../../../common/component/FatOutlineButton';

type Props = {
    isShown: boolean;
    /** True while the resend is running. Both answers are already given; neither can be given again. */
    busy: boolean;
    /** The backend's own wording for what already exists, so the user knows what they are overriding. */
    message: string;
    onConfirmed: () => void;
    onDismissed: () => void;
};

/**
 * Asks before writing a second contact that shares an email or a phone with one that already exists
 * (BHPNPP-3241). Two people at the same practice really do share a switchboard number, so this is a
 * question rather than a refusal.
 *
 * <p>Only the soft duplicate codes reach here. Sharing a name and an email, or a name and a phone,
 * is a hard stop that no click can clear, and it shows as a plain error instead.
 *
 * <p>The two buttons report and nothing else: whether the dialog is up is the caller's state, not
 * this component's. Evergreen fires onCloseComplete after a 240ms exit animation, so letting the
 * animation clear the caller's state would wipe a second duplicate that a fast resend had already
 * raised, and the save would vanish with no message anywhere.
 *
 * <p>Both answers grey out while the resend runs. The dialog stays up for the whole of it, and a
 * second answer given in that window would either re-send the contact or close a question about one
 * that is already being written. The hook refuses both regardless; this is what says so on screen.
 */
export const DuplicateContactDialog = memo(function DuplicateContactDialog(
    {isShown, busy, message, onConfirmed, onDismissed}: Props) {
    return (
        <Dialog
            title="Possible duplicate"
            isShown={isShown}
            width={510}
            hasClose={false}
            shouldCloseOnOverlayClick={false}
            shouldCloseOnEscapePress={false}
            contentContainerProps={{textAlign: 'center'}}
            footer={() => (
                // minus the default evergreen-ui padding in footer container
                <Pane width={510 - 16 * 2} className="flex justify-evenly">
                    <Pane width={140}>
                        <FatOutlineButton color="green" text="Save Anyway" onClick={onConfirmed} disabled={busy}/>
                    </Pane>
                    <Pane width={140}>
                        <FatOutlineButton color="red" text="Go Back" onClick={onDismissed} disabled={busy}/>
                    </Pane>
                </Pane>
            )}
        >
            <Paragraph>{message}</Paragraph>
            <Paragraph>Save this contact anyway?</Paragraph>
        </Dialog>
    );
});
